import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicantAPI } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { User, Brain, Eye, Sparkles, MapPin, Briefcase, Video, Play } from 'lucide-react';
import SkillWordCloud from '@/components/shared/SkillWordCloud';
import HotTagWidget from '@/components/shared/HotTagWidget';

export default function ApplicantHome() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [pendingInterviews, setPendingInterviews] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const p = await applicantAPI.getProfile(); setProfile(p); setHasProfile(true); } catch { setHasProfile(false); }
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interviews/my-pending`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        setPendingInterviews(d.interviews || []);
      } catch { /* ignore */ }
    };
    load();
    const onJayUpdate = () => load();
    window.addEventListener('jay:profile-updated', onJayUpdate);
    return () => window.removeEventListener('jay:profile-updated', onJayUpdate);
  }, []);

  return (
    <div data-testid="applicant-home">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Welcome back</h1>
      <p className="text-slate-500 dark:text-zinc-500 mb-8">Here&apos;s what&apos;s happening with your profile</p>

      <HotTagWidget />

      {hasProfile === false && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6">
          <p className="text-amber-700 dark:text-amber-300 text-sm">You haven&apos;t set up your profile yet.</p>
          <button onClick={() => navigate('/applicant/profile')} className="text-amber-600 dark:text-amber-400 text-sm underline mt-1" data-testid="setup-profile-btn">Set up now</button>
        </div>
      )}

      {/* Clickable stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <ActionCard icon={Eye} label="Visibility" value={profile?.visibility_status?.replace('_', ' ') || 'Not set'} color="emerald" onClick={() => navigate('/applicant/profile')} hint="Edit profile" />
        <ActionCard icon={MapPin} label="My Journey" value="Track" color="blue" onClick={() => navigate('/applicant/journey')} hint="View journey" />
        <ActionCard icon={Brain} label="Interview Prep" value="Practice" color="amber" onClick={() => navigate('/applicant/prep')} hint="Start a session" />
        <ActionCard icon={Sparkles} label="Job Matches" value="View" color="purple" onClick={() => navigate('/applicant/job-matches')} hint="AI matches" />
      </div>

      {/* Pending Interviews */}
      {pendingInterviews.length > 0 && (
        <div className="mb-8" data-testid="pending-interviews">
          <h3 className="text-slate-900 dark:text-white font-medium mb-3 flex items-center gap-2"><Video className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Pending Interviews</h3>
          <div className="grid gap-3">
            {pendingInterviews.map(iv => (
              <div key={iv.id} className="bg-white dark:bg-[#111114] border border-blue-500/20 rounded-xl p-4 flex items-center justify-between hover:border-blue-500/40 transition-colors">
                <div>
                  <p className="text-slate-900 dark:text-white font-medium text-sm">{iv.job_title}</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">{iv.company || 'Company'} &middot; {iv.question_count} questions &middot; Toughness {iv.toughness}/10</p>
                </div>
                <button onClick={() => navigate(`/applicant/interview/${iv.id}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500 transition-colors" data-testid={`start-interview-${iv.id}`}>
                  <Play className="w-4 h-4" /> Start Interview
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick action buttons */}
      <div className="flex flex-wrap gap-2 mb-8">
        <QuickBtn icon={User} label="Edit Profile" onClick={() => navigate('/applicant/profile')} />
        <QuickBtn icon={MapPin} label="My Journey" onClick={() => navigate('/applicant/journey')} />
        <QuickBtn icon={Briefcase} label="Job Matches" onClick={() => navigate('/applicant/job-matches')} />
        <QuickBtn icon={Brain} label="Interview Prep" onClick={() => navigate('/applicant/prep')} />
      </div>

      {profile && (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-6 mb-6 cursor-pointer hover:border-slate-300 dark:hover:border-zinc-700/50 transition-colors" onClick={() => navigate('/applicant/profile')}>
          <h3 className="text-slate-900 dark:text-white font-medium mb-4">Profile Summary <span className="text-slate-500 dark:text-zinc-600 text-xs ml-2">Click to edit</span></h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-slate-500 dark:text-zinc-500">Name:</span> <span className="text-slate-600 dark:text-zinc-300">{profile.personal_info?.name || 'Not set'}</span></div>
            <div><span className="text-slate-500 dark:text-zinc-500">Email:</span> <span className="text-slate-600 dark:text-zinc-300">{profile.personal_info?.email || user?.email}</span></div>
            <div><span className="text-slate-500 dark:text-zinc-500">Skills:</span> <span className="text-slate-600 dark:text-zinc-300">{profile.skills?.length || 0} listed</span></div>
            <div><span className="text-slate-500 dark:text-zinc-500">Experience:</span> <span className="text-slate-600 dark:text-zinc-300">{profile.work_experience?.length || 0} entries</span></div>
          </div>
        </div>
      )}

      <SkillWordCloud />
    </div>
  );
}

function ActionCard({ icon: Icon, label, value, color, onClick, hint }) {
  const colors = { emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/15', blue: 'text-blue-600 dark:text-blue-400 bg-blue-500/10 hover:bg-blue-500/15', amber: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/15', purple: 'text-purple-600 dark:text-purple-400 bg-purple-500/10 hover:bg-purple-500/15' };
  return (
    <button onClick={onClick} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 text-left hover:border-slate-300 dark:hover:border-zinc-700/50 transition-all group" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${colors[color]}`}><Icon className="w-4 h-4" /></div>
        <div>
          <p className="text-slate-500 dark:text-zinc-500 text-xs">{label}</p>
          <p className="text-slate-900 dark:text-white font-semibold capitalize">{value}</p>
        </div>
      </div>
      <p className="text-slate-500 dark:text-zinc-600 text-[10px] mt-2 group-hover:text-slate-500 dark:group-hover:text-zinc-400 transition-colors">{hint} &rarr;</p>
    </button>
  );
}

function QuickBtn({ icon: Icon, label, onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-zinc-800/50 border border-slate-200 dark:border-zinc-800 rounded-lg text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-zinc-600 transition-colors" data-testid={`quick-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
